import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Cross, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PrayerForm = () => {
  const [form, setForm] = useState({
    name: "",
    contact: "",
    prayer_need: "",
    wants_callback: false,
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) =>
    setForm({
      ...form,
      [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.contact || !form.prayer_need) {
      toast.error("Please share your name, contact, and what we're praying for.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/prayer`, form);
      toast.success("Request received. We're praying with you.");
      setForm({ name: "", contact: "", prayer_need: "", wants_callback: false });
    } catch (err) {
      console.error(err);
      toast.error("Message didn't send. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="prayer"
      data-testid="prayer-section"
      className="relative py-24 md:py-32 bg-[#0a0a0a] grain overflow-hidden"
    >
      <div className="absolute top-0 right-0 w-[50vw] h-[50vw] bg-[#D4AF37]/5 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-[1100px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="text-center mb-14 max-w-2xl mx-auto">
          <Cross size={36} className="mx-auto text-[#D4AF37] mb-6" />
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="h-px w-10 bg-[#D4AF37]" />
            <span className="text-xs uppercase tracking-[0.35em] text-[#D4AF37]">
              Prayer · Talk · Stand With You
            </span>
            <span className="h-px w-10 bg-[#D4AF37]" />
          </div>
          <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tighter">
            Need someone to pray with you?
          </h2>
          <p className="mt-6 text-white/65 leading-relaxed">
            Jesus still heals. Jesus still delivers. If you need prayer, a
            conversation, or just somebody to stand with you in faith — send a
            note. Nothing is too small and nothing is too heavy.
          </p>
        </div>

        <form
          data-testid="prayer-form"
          onSubmit={onSubmit}
          className="bg-[#0e0e0e] border border-white/10 p-8 md:p-10 space-y-6 max-w-2xl mx-auto"
        >
          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">
              Your name <span className="text-[#D4AF37]">*</span>
            </span>
            <input
              data-testid="prayer-name-input"
              value={form.name}
              onChange={update("name")}
              placeholder="First name is fine"
              className="prayer-input"
            />
          </label>

          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">
              Email or phone <span className="text-[#D4AF37]">*</span>
            </span>
            <input
              data-testid="prayer-contact-input"
              value={form.contact}
              onChange={update("contact")}
              placeholder="So we can reach you"
              className="prayer-input"
            />
          </label>

          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">
              What can we pray for? <span className="text-[#D4AF37]">*</span>
            </span>
            <textarea
              data-testid="prayer-need-input"
              value={form.prayer_need}
              onChange={update("prayer_need")}
              rows={6}
              placeholder="Share as much or as little as you want. Safe space."
              className="prayer-input resize-none"
            />
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              data-testid="prayer-callback-checkbox"
              type="checkbox"
              checked={form.wants_callback}
              onChange={update("wants_callback")}
              className="mt-1 accent-[#D4AF37]"
            />
            <span className="text-sm text-white/70">
              I'd like to talk / have someone pray with me directly.
            </span>
          </label>

          <button
            data-testid="prayer-submit-button"
            type="submit"
            disabled={loading}
            className="inline-flex items-center gap-3 h-12 px-8 bg-[#D4AF37] text-black text-sm uppercase tracking-[0.2em] font-semibold hover:bg-[#F1C40F] disabled:opacity-60 transition-colors"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : null}
            {loading ? "Sending…" : "Send Prayer Request"}
          </button>

          <p className="text-xs text-white/40 leading-relaxed">
            Your request stays private. We read every message and pray
            personally.
          </p>
        </form>
      </div>

      <style>{`
        .prayer-input {
          width: 100%;
          background: #050505;
          border: 1px solid rgba(255,255,255,0.12);
          color: #fff;
          padding: 14px 16px;
          font-size: 14px;
          font-family: inherit;
          outline: none;
          transition: border-color 0.2s;
        }
        .prayer-input:focus { border-color: #D4AF37; }
        .prayer-input::placeholder { color: rgba(255,255,255,0.3); }
      `}</style>
    </section>
  );
};
